import type { RendererProps } from "../core/element-registry"
import type { CSSProperties } from "react"
import { cn } from "../lib/utils"

type MapMarker = {
  id?: string
  label?: string
  title?: string
  name?: string
  latitude?: number
  longitude?: number
  lat?: number
  lng?: number
  x?: number
  y?: number
}

const DEFAULT_MARKERS: MapMarker[] = [
  { id: "bangkok", label: "Bangkok", x: 58, y: 54 },
]

export function MapRenderer({ element }: RendererProps) {
  const props = element as unknown as Record<string, unknown>
  const markers = normalizeMarkers(props.markers ?? props.locations ?? props.items)
  const centerLabel = String(props.label ?? props.title ?? props.text ?? "Map")

  return (
    <div
      className={cn(
        "relative min-h-[220px] w-full overflow-hidden rounded-lg border border-border bg-[linear-gradient(135deg,#f8f5ee_0%,#ebe4d7_45%,#dfe8df_100%)]",
        element.className,
      )}
      style={element.style as CSSProperties | undefined}
      data-atom-type="map"
      data-map-renderer="static"
      aria-label={centerLabel}
      role="img"
    >
      <div className="absolute inset-0 opacity-45 [background-image:linear-gradient(rgba(80,71,58,.16)_1px,transparent_1px),linear-gradient(90deg,rgba(80,71,58,.16)_1px,transparent_1px)] [background-size:40px_40px]" />
      <div className="absolute left-[8%] top-[18%] h-[58%] w-[72%] rounded-[48%] border border-[#a8b49c]/50 bg-[#cbd9bf]/35 blur-[1px]" />
      <div className="absolute bottom-0 left-0 h-[42%] w-full bg-[#d7c7ae]/35 [clip-path:polygon(0_46%,18%_32%,32%_55%,47%_38%,61%_62%,79%_44%,100%_58%,100%_100%,0_100%)]" />
      <div className="absolute left-[12%] top-[62%] h-1 w-[78%] rotate-[-8deg] rounded-full bg-[#ffffff]/70 shadow-sm" />
      <div className="absolute left-[18%] top-[38%] h-1 w-[62%] rotate-[25deg] rounded-full bg-[#ffffff]/60 shadow-sm" />

      <div className="absolute left-4 top-4 rounded-md border border-black/10 bg-white/85 px-3 py-2 text-xs shadow-sm backdrop-blur">
        <div className="font-medium text-neutral-900">{centerLabel}</div>
        <div className="text-neutral-600">{markers.length} marker{markers.length === 1 ? "" : "s"}</div>
      </div>

      {markers.map((marker, index) => {
        const point = markerPoint(marker, index, markers.length)
        const label = marker.label ?? marker.title ?? marker.name ?? `Location ${index + 1}`
        return (
          <div
            key={marker.id ?? label}
            className="absolute -translate-x-1/2 -translate-y-full"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
            data-map-marker={marker.id ?? label}
          >
            <div className="relative flex flex-col items-center">
              <div className="rounded-md bg-neutral-950 px-2 py-1 text-[11px] font-medium text-white shadow-md">
                {label}
              </div>
              <div className="h-3 w-3 rotate-45 rounded-[2px] bg-neutral-950" />
              <div className="mt-[-2px] h-3 w-3 rounded-full border-2 border-white bg-[#d4ad5f] shadow" />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function normalizeMarkers(value: unknown): MapMarker[] {
  if (!Array.isArray(value)) return DEFAULT_MARKERS
  return value.filter((item): item is MapMarker => item != null && typeof item === "object")
}

function markerPoint(marker: MapMarker, index: number, total: number): { x: number; y: number } {
  if (typeof marker.x === "number" && typeof marker.y === "number") {
    return { x: clamp(marker.x), y: clamp(marker.y) }
  }
  const lat = typeof marker.latitude === "number" ? marker.latitude : marker.lat
  const lng = typeof marker.longitude === "number" ? marker.longitude : marker.lng
  if (typeof lat === "number" && typeof lng === "number") {
    return {
      x: clamp(((lng + 180) / 360) * 100),
      y: clamp(((90 - lat) / 180) * 100),
    }
  }
  const spread = total <= 1 ? 0 : index / (total - 1)
  return {
    x: 24 + spread * 52,
    y: 58 - Math.sin(index + 1) * 18,
  }
}

function clamp(value: number): number {
  return Math.max(8, Math.min(92, value))
}
